import { useEffect } from 'react';
import { useStore } from '@nanostores/react';
import {
  Banner,
  Button,
  Card,
  Heading,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  VStack,
} from '@astryxdesign/core';

import { PlanCards, usePlanCheckout } from '../components/PlanCards';
import { formatLimit, formatPrice, formatQuota, type Plan } from '../lib/api';
import { $auth } from '../stores/auth';
import { $plans, $plansError, $plansLoading, loadPlans } from '../stores/plans';
import { usePageEvent } from '../analytics/usePageEvent';

export function PricingPage() {
  usePageEvent('pricing_viewed');
  const plans = useStore($plans);
  const loading = useStore($plansLoading);
  const error = useStore($plansError);
  const auth = useStore($auth);
  const { isAuthed, onSelect, pendingCode, scheduledCode, scheduledNotice } = usePlanCheckout();

  useEffect(() => {
    void loadPlans();
  }, []);

  return (
    <div className="shell section-block">
      <VStack gap={5}>
        <div className="pricing-head">
          <span className="eyebrow">Pricing</span>
          <Heading level={1} className="display--md">
            Same weights. <span className="gradient-text">Pay for volume.</span>
          </Heading>
          <p className="lede measure--wide">
            Every plan runs the same TimesFM 2.5 weights. What changes is throughput: calls per month,
            requests per minute, and how large a single request may be. These numbers are read straight
            from the API, so what you see here is what the server enforces.
          </p>
        </div>

        {error && <Banner status="error" title="Could not load plans" description={error} />}

        {auth.plan && (
          <Banner
            status={scheduledNotice ? 'warning' : 'info'}
            title={`You are on ${auth.plan.name}`}
            description={
              scheduledNotice ??
              'Upgrades take effect immediately and are prorated. Downgrades wait for your renewal date.'
            }
            endContent={<Button label="Plan & billing" variant="secondary" size="sm" href="/app/billing" />}
          />
        )}

        {loading && plans.length === 0 ? (
          <Spinner size="lg" label="Loading plans" />
        ) : (
          <PlanCards
            plans={plans}
            currentCode={auth.plan?.code}
            pendingCode={pendingCode}
            scheduledCode={scheduledCode}
            onSelect={onSelect}
            ctaHref={isAuthed ? undefined : '/register'}
          />
        )}

        {plans.length > 0 && <ComparisonTable plans={plans} />}

        <Card padding={3} variant="muted">
          <VStack gap={1}>
            <Text weight="semibold">How metering works</Text>
            <Text size="sm" color="secondary">
              One HTTP call to <span className="mono">POST /v1/forecast</span> counts as one request,
              whether it carries 1 series or 512. Requests rejected for a validation error, a rate
              limit, or an upstream failure are not billed. The counter resets at the start of each UTC
              month; every response carries the live numbers in{' '}
              <span className="mono">X-Quota-Used</span> and <span className="mono">X-Quota-Limit</span>.
            </Text>
          </VStack>
        </Card>
      </VStack>
    </div>
  );
}

function ComparisonTable({ plans }: { plans: Plan[] }) {
  const rows: Array<{ label: string; value: (p: Plan) => string }> = [
    { label: 'Price / month', value: (p) => (p.contact_sales ? 'Custom' : formatPrice(p.price_cents, p.currency)) },
    { label: 'Forecast calls / month', value: (p) => formatQuota(p.monthly_quota) },
    { label: 'Requests / minute', value: (p) => String(p.rate_limit_rpm) },
    { label: 'Burst size', value: (p) => String(p.burst_size) },
    { label: 'Series per request', value: (p) => formatLimit(p.max_series, p.contact_sales) },
    { label: 'Context window', value: (p) => `${formatLimit(p.max_context, p.contact_sales)} points` },
    { label: 'Max horizon', value: (p) => `${p.max_horizon} steps` },
    { label: 'API keys', value: (p) => formatLimit(p.max_keys, p.contact_sales) },
    { label: 'Quantile bands', value: (p) => (p.quantiles ? 'Yes' : 'No') },
    { label: 'Covariates', value: (p) => (p.covariates ? 'Yes' : 'No') },
    { label: 'Support & SLA', value: (p) => p.support_level || '—' },
  ];

  return (
    <VStack gap={2}>
      <span className="eyebrow">Compare</span>
      <Heading level={2} className="section-title">
        Side by side
      </Heading>
      <Table density="balanced" dividers="rows" hasHover>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Limit</TableHeaderCell>
            {plans.map((p) => (
              <TableHeaderCell key={p.code}>{p.name}</TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell>
                <Text size="sm" color="secondary">
                  {row.label}
                </Text>
              </TableCell>
              {plans.map((p) => (
                <TableCell key={p.code}>
                  <Text size="sm">{row.value(p)}</Text>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </VStack>
  );
}
